import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ScanLine, Users, Package, Box, Trash2, Send, CheckCircle, AlertCircle, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { validateBundleFormat } from '@/utils/validation';
import StaffIdInput from '@/components/StaffIdInput';
import { validateAssortmentPackingUsn, submitAssortmentPacking, getFobSettings } from '@/services/fobSectionApi';
import PrepastedAssortmentPacking from './PrepastedAssortmentPacking';

const AssortmentPacking = () => {
  const navigate = useNavigate();

  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}]
  const [usnCode, setUsnCode] = useState('');
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [sessionUsns, setSessionUsns] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prepastedMode, setPrepastedMode] = useState(null);

  const staffInputRef = useRef(null);
  const usnInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  useEffect(() => {
    staffInputRef.current?.focus();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await getFobSettings();
        setPrepastedMode(!!data.prepasted_usn_mode);
      } catch {
        setPrepastedMode(false);
      }
    })();
  }, []);

  if (prepastedMode === null) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2D5F4F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (prepastedMode) {
    return <PrepastedAssortmentPacking />;
  }

  const handleStaffValidated = (result) => {
    if (!result.valid) return;
    if (staffList.some(s => s.staff_id === result.staffId)) {
      toast.error('This staff is already added');
      return;
    }
    setStaffList(prev => [...prev, { staff_id: result.staffId, staff_name: result.staffName || '' }]);
    setTimeout(() => staffInputRef.current?.focus(), 100);
  };

  const handleRemoveStaff = (staffId) => {
    if (sessionUsns.length > 0) {
      toast.error('Clear the current session before changing staff');
      return;
    }
    setStaffList(prev => prev.filter(s => s.staff_id !== staffId));
  };

  const handleUsnScan = async (e) => {
    e.preventDefault();
    const scanned = usnCode.trim();
    if (!scanned) {
      usnInputRef.current?.focus();
      return;
    }
    if (sessionUsns.includes(scanned)) {
      toast.error('This USN is already scanned in this session');
      setUsnCode('');
      usnInputRef.current?.focus();
      return;
    }
    setIsProcessing(true);
    try {
      await validateAssortmentPackingUsn(scanned);
      setSessionUsns(prev => [scanned, ...prev]);
      setUsnCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid USN');
      setUsnCode('');
    } finally {
      setIsProcessing(false);
      setTimeout(() => usnInputRef.current?.focus(), 50);
    }
  };

  const handleRemoveUsn = (usn) => {
    setSessionUsns(prev => prev.filter(u => u !== usn));
    setTimeout(() => usnInputRef.current?.focus(), 50);
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    const scannedBundle = bundleBarcode.trim();
    if (!scannedBundle) {
      setTimeout(() => bundleInputRef.current?.focus(), 50);
      return;
    }
    const formatValidation = validateBundleFormat(scannedBundle);
    if (!formatValidation.valid) {
      toast.error(formatValidation.error);
      setBundleBarcode('');
      setTimeout(() => bundleInputRef.current?.focus(), 50);
      return;
    }
    if (sessionUsns.length === 0) {
      toast.error('Scan at least one USN before adding bundle');
      setBundleBarcode('');
      setTimeout(() => usnInputRef.current?.focus(), 50);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitAssortmentPacking(staffList, scannedBundle, sessionUsns);
      const breakdown = (result.staff_breakdown || [])
        .map(s => `${s.staff_name || s.staff_id}: ${s.attributed_qty}`).join(', ');
      toast.success(
        <div>
          <p className="font-semibold">Assortment Packing Complete!</p>
          <p className="text-sm">{sessionUsns.length} pieces → {scannedBundle}</p>
          {breakdown && <p className="text-xs mt-1">Split: {breakdown}</p>}
        </div>,
        { duration: 5000 }
      );
      setBundleBarcode('');
      setSessionUsns([]);
      setTimeout(() => usnInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete assortment packing');
      setBundleBarcode('');
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearSession = () => {
    if (sessionUsns.length === 0) return;
    if (!window.confirm(`Clear all ${sessionUsns.length} scanned USNs?`)) return;
    setSessionUsns([]);
    setBundleBarcode('');
    setTimeout(() => usnInputRef.current?.focus(), 100);
  };

  const hasStaff = staffList.length > 0;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sections/fob')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Assortment Packing</h1>
              <p className="text-sm text-[#E8DCC8]">FOB Section - Combined multi-staff operation</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Step 1: Staff (multi) */}
        <Card className={`border-2 ${hasStaff ? 'border-green-500 bg-green-50/30' : 'border-[#E5DFD0]'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${hasStaff ? 'bg-green-500' : 'bg-[#2D5F4F]'}`}>
                1
              </div>
              <Users className="w-5 h-5 text-[#2D5F4F]" />
              Staff (add one or more)
            </CardTitle>
            <CardDescription>
              Scan each staff ID that worked this bundle. Quantity is split equally for payroll.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <StaffIdInput
              inputRef={staffInputRef}
              onValidated={handleStaffValidated}
              autoFocus={true}
              label=""
            />
            {hasStaff && (
              <div className="flex flex-wrap gap-2" data-testid="staff-chips">
                {staffList.map((s) => (
                  <div
                    key={s.staff_id}
                    className="flex items-center gap-2 bg-[#2D5F4F] text-white px-3 py-1.5 rounded-lg"
                    data-testid={`staff-chip-${s.staff_id}`}
                  >
                    <span className="font-mono text-sm">{s.staff_id}</span>
                    {s.staff_name && <span className="text-xs text-[#E8DCC8]">{s.staff_name}</span>}
                    <button
                      onClick={() => handleRemoveStaff(s.staff_id)}
                      className="hover:text-red-300"
                      data-testid={`remove-staff-${s.staff_id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Scan USNs */}
        {hasStaff && (
          <Card className="border-2 border-[#2D5F4F]">
            <CardHeader className="pb-3 bg-[#2D5F4F]/5">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-[#2D5F4F]">
                  2
                </div>
                <ScanLine className="w-5 h-5 text-[#2D5F4F]" />
                Scan Products (USN)
              </CardTitle>
              <CardDescription>Scan each piece&apos;s USN sticker</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleUsnScan} className="flex gap-3">
                <Input
                  ref={usnInputRef}
                  value={usnCode}
                  onChange={(e) => setUsnCode(e.target.value)}
                  placeholder="Scan USN barcode..."
                  className="flex-1 text-lg h-14 border-2 border-[#2D5F4F] font-mono"
                  disabled={isProcessing || isSubmitting}
                  data-testid="usn-input"
                  autoComplete="off"
                />
                <Button
                  type="submit"
                  className="h-14 px-8 bg-[#2D5F4F] hover:bg-[#234A3D] text-lg"
                  disabled={isProcessing || isSubmitting}
                  data-testid="scan-usn-btn"
                >
                  {isProcessing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Plus className="w-5 h-5 mr-2" />Add</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Session summary */}
        {hasStaff && (
          <Card className="border-2 border-[#E5DFD0] bg-gradient-to-r from-[#2D5F4F]/5 to-transparent">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A5F6E] mb-1">Products Scanned</p>
                  <p className="text-5xl font-bold text-[#2D5F4F]" data-testid="usn-count">
                    {sessionUsns.length}
                  </p>
                  <p className="text-sm text-[#5A5F6E] mt-1">
                    Split across {staffList.length} staff (~{Math.floor(sessionUsns.length / staffList.length)} each)
                  </p>
                </div>
                {sessionUsns.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleClearSession}
                    className="h-12 px-6 border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="clear-session-btn"
                    disabled={isSubmitting}
                  >
                    <Trash2 className="w-5 h-5 mr-2" />Clear All
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Bundle */}
        {hasStaff && sessionUsns.length > 0 && (
          <Card className="border-2 border-orange-400 bg-orange-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-orange-500">
                  3
                </div>
                <Box className="w-5 h-5 text-orange-600" />
                Scan Bundle QR Code to Complete
              </CardTitle>
              <CardDescription>
                Scan the NEW bundle QR to pack all {sessionUsns.length} pieces
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBundleScan} className="flex gap-3">
                <Input
                  ref={bundleInputRef}
                  value={bundleBarcode}
                  onChange={(e) => setBundleBarcode(e.target.value)}
                  placeholder="Scan Bundle QR code..."
                  className="flex-1 text-lg h-14 border-2 border-orange-400 font-mono bg-white"
                  disabled={isSubmitting}
                  data-testid="bundle-barcode-input"
                  autoComplete="off"
                />
                <Button
                  type="submit"
                  className="h-14 px-8 bg-orange-500 hover:bg-orange-600 text-lg"
                  disabled={isSubmitting}
                  data-testid="complete-btn"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Complete
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Scanned list */}
        {hasStaff && sessionUsns.length > 0 && (
          <Card className="border-2 border-[#E5DFD0]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-[#5A5F6E]" />Scanned Products
              </CardTitle>
              <CardDescription>{sessionUsns.length} pieces ready</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {sessionUsns.map((usn, index) => (
                  <div
                    key={usn}
                    className="flex items-center justify-between p-3 bg-[#F5F1E8] rounded-lg"
                    data-testid={`usn-item-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-[#5A5F6E] w-8">#{sessionUsns.length - index}</span>
                      <p className="font-mono font-semibold text-[#1A1F2E]">{usn}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveUsn(usn)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-usn-${index}`}
                      disabled={isSubmitting}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {hasStaff && sessionUsns.length === 0 && (
          <Card className="border-2 border-dashed border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <ScanLine className="w-16 h-16 mx-auto text-[#E5DFD0] mb-4" />
              <p className="text-[#5A5F6E]">No products scanned yet</p>
              <p className="text-sm text-[#5A5F6E]/70 mt-1">Start scanning USN stickers</p>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="border border-[#E5DFD0] bg-blue-50/50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">How Assortment Packing Works:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Add all staff working this bundle (one or more)</li>
                  <li>Scan all USN stickers for the bundle</li>
                  <li>Scan the Bundle QR code to complete</li>
                </ol>
                <p className="mt-2 text-blue-600 text-xs">
                  This is recorded as ONE combined operation for payroll. Quantity is split equally
                  among staff (remainder to earliest); reports still show the per-staff split.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AssortmentPacking;
